import styled, { css, keyframes } from "styled-components";
import type { Environments, Fixed } from "./types";

// z-index 2000 on purpose: above the design system, which tops out at 1999,
// and below what the apps put at 9999. A banner that outranks everything is a
// banner that covers the footer.
const fixedBottom = css`
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 2000;
`;

const pulse = keyframes`
  0%   { filter: brightness(50%); }
  50%  { filter: brightness(120%); }
  100% { filter: brightness(50%); }
`;

const getBannerBackground = (environment: Environments) => {
  switch (String(environment).toLowerCase()) {
    case "int":
    case "aws-int":
    case "qa":
      return "linear-gradient(90deg, #FFA726, #FB8C00)";
    case "stg":
    case "aws-stg":
    case "staging":
      return "linear-gradient(90deg, #c0392b, #e74c3c)";
    default:
      return "linear-gradient(90deg, #B0BEC5, #90A4AE)";
  }
};

export const Banner = styled.div<{
  fixed?: Fixed;
  canChangeEnv?: boolean;
  environment: Environments;
}>`
  position: relative;
  display: flex;
  justify-content: center;
  text-align: center;
  color: #ffffff;
  width: 100%;
  padding: 0 10px;
  flex-direction: column;

  ${({ fixed }) => fixed === "bottom" && fixedBottom}

  ${({ canChangeEnv }) =>
    canChangeEnv &&
    css`
      cursor: pointer;
    `}

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ environment }) => getBannerBackground(environment)};
    z-index: 0;
    animation: ${pulse} 2s infinite;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 0;
`;

export const BannerText = styled.div<{ hasDivisor?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: 2px;

  ${({ hasDivisor }) =>
    hasDivisor &&
    css`
      border-left: 1px solid #ffffff;
      padding-left: 10px;
      margin-left: 10px;
    `}
`;

export const Dismiss = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 32px;
  z-index: 2;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 14px;
  cursor: pointer;
`;
